import { FC } from 'react'
import './LoadingBar.scss'

interface LoadingBarType {
  kind?: 'bar'
  percentage?: number
}

interface LoadingCircle {
  kind?: 'circle'
  percentage?: undefined
}

export const LoadingBar: FC<
  {
    className?: string
    colorKind?:
      | 'primary'
      | 'secondary'
      | 'tertiary'
      | 'grey'
      | 'success'
      | 'warning'
      | 'danger'
  } & (LoadingBarType | LoadingCircle)
> = ({ className, kind = 'bar', percentage, colorKind = 'grey' }) => {
  const hasPercentage = () => {
    if (typeof percentage === 'number') {
      return {
        width: percentage,
        animation: 'basic-transition',
      }
    }
    return {
      width: undefined,
      animation: 'is-flowing',
    }
  }

  const circleRadius = 50
  const circumference = circleRadius * 2 * Math.PI

  return (
    <div
      className={`LoadingBar m-auto w-full ${
        kind === 'bar' ? 'LoadingBar--is-bar' : ''
      } ${kind === 'circle' ? 'LoadingBar--is-circle' : ''} ${className}`}
      style={
        kind === 'bar'
          ? { backgroundColor: `var(--color-${colorKind}-200)` }
          : {}
      }
    >
      {kind === 'bar' && (
        <div
          className={`LoadingBar__bar LoadingBar__bar--${
            hasPercentage().animation
          } ${className}`}
          style={{
            width: `${hasPercentage().width}%`,
            backgroundColor: `var(--color-${colorKind}-400)`,
          }}
        />
      )}
      {kind === 'circle' && (
        <svg
          viewBox="0 0 110 110"
          className={`LoadingBar__circle-svg ${className}`}
        >
          <circle
            r={circleRadius}
            cx={55}
            cy={55}
            fill={`var(--color-${colorKind}-400)`}
            className="LoadingBar__circle"
            stroke={`var(--color-${colorKind}-100)`}
          />
          <circle
            r={circleRadius}
            cx={55}
            cy={55}
            className="LoadingBar__circle--grow"
            stroke={`var(--color-${colorKind}-200)`}
            style={{
              strokeDasharray: circumference,
              strokeDashoffset: circumference * 2,
            }}
          />
          <circle
            r={circleRadius}
            cx={55}
            cy={55}
            stroke={`var(--color-${colorKind}-400)`}
            className="LoadingBar__circle--band"
            style={{
              strokeDasharray: `30 ${circumference}`,
            }}
          />
          <g transform="scale(1.35) translate(-40, 12) rotate(90 55 55)">
            <path
              fill={`var(--color-${colorKind}-050)`}
              d="M16.7,31.9C16.7,31.9,16.7,31.8,16.7,31.9c0.2,0.1,0.4,0.3,0.6,0.5c-0.2,0.2-0.1,0.8,0,1.1 C17,33,16.9,32.6,16.7,31.9 M18.8,34.4c0.1,0,0.2,0.1,0.3,0.1c0-0.1,0-0.2,0-0.3l0,0C19,34.3,18.9,34.4,18.8,34.4z M31.7,40.1 c0.4,0.4,0.5,1,0.3,2c0.5-0.4,0.8-1.2,0.8-1.8c0-0.3-0.2-0.7-0.5-0.8C32.2,39.7,32,39.9,31.7,40.1z M31,41.1c0.2,0.2,0.5,0.6,0.6,1	c0.1-0.6,0.2-1.5,0-1.8c-0.1,0.1-0.1,0.2-0.2,0.2C31.3,40.7,31.2,40.9,31,41.1z M30.8,42.4c0.1,0.2,0.1,0.4,0.1,0.6	c0.2-0.2,0.3-0.3,0.4-0.6s-0.1-0.8-0.4-1.1c-0.1,0.2-0.2,0.4-0.3,0.6C30.7,42,30.8,42.2,30.8,42.4L30.8,42.4z M31.7,42.7 c0.2,0.2,0.4,0.7,0.4,1c0.3-0.1,0.5-0.4,0.6-0.6c0-0.3-0.1-0.5-0.3-0.7c-0.1,0-0.3,0.1-0.4,0.1C31.9,42.6,31.9,42.6,31.7,42.7 L31.7,42.7z M30.1,43.3c0,0.1,0,0.2,0,0.2h0.1l0,0c0.6-0.3,0.5-0.8,0.2-1.5c-0.2,0.3-0.3,0.6-0.5,1C30,43.2,30.1,43.2,30.1,43.3 M29.9,43.3c-0.1,0.2-0.1,0.4-0.2,0.6C30,43.7,29.9,43.7,29.9,43.3 M29.6,44.2c-0.1,0.2-0.1,0.5-0.2,0.7c0.2,0,0.4-0.1,0.4-0.2 C29.9,44.6,29.8,44.4,29.6,44.2C29.7,44.2,29.6,44.2,29.6,44.2z M30.2,44.5c0.2,0.1,0.4,0,0.5-0.1c0.1-0.2,0.1-0.5-0.1-0.8 c-0.1,0-0.3,0.1-0.4,0.1c0,0.2-0.1,0.3-0.2,0.4C30.1,44.2,30.2,44.3,30.2,44.5z M31.2,44.1L31.2,44.1c0.2,0,0.6-0.1,0.6-0.2l0,0 c0.1-0.2-0.1-0.7-0.2-0.9c-0.1,0.3-0.3,0.3-0.6,0.3h-0.1c0,0.1-0.1,0.1-0.1,0.2C31,43.6,31.2,44,31.2,44.1z M17.8,33.5 c0.1,0.3,0.4,0.4,0.5,0.1C18.3,33,17.6,33,17.8,33.5 M30.8,32.9c0,0.1,0.1,0.2,0.2,0.2c0.2-0.2,0.3-0.5,0.4-0.7 C31.2,31.9,30.6,32.6,30.8,32.9 M27.3,34c0.9,0.5,2.5,0.8,3.4,0.1c-0.3-0.2-0.5-0.4-0.6-0.8c-0.1-0.4,0-1,0.3-1.4L30.3,32l0,0 C29.7,32.5,28.4,33.5,27.3,34z M32.5,29.7c-0.3,0.4-0.8,0.9-1.2,1.3c0.3-0.2,0.8-0.1,1,0.2C32.4,30.6,32.3,30,32.5,29.7 M39.1,21.1 c-0.8,0.6-1.3,2.9-1.1,4c0,1.8,0.9,2.7,2.6,2.7c3.8,0,9.2-4.4,9.9-7.2s-0.4-5.1-1.9-7.4c-1.7-2.6-3.3-5-1.5-7.5 c-1.3,0.4-2.4,1.5-2.8,2.8c-0.4,1-0.2,2.1,0.4,3c0.2,0.3,0.6,0.7,1,1.1c0.8,0.8,1.8,1.6,1.7,2.5l0,0c0,0,0,0-0.1,0 c-0.1-0.6-1-1.5-1.7-2.1c1.3,1.8,1.2,4,0,5.9C44,21,41.4,21.9,39.1,21.1z M13.3,9.6C13,10.3,12.5,11,12,11.7c-0.4,0.5-0.7,1-1.1,1.5 c-1.6,2.4-1.8,5.1-0.6,7.3s3.6,3.5,6.4,3.4c-0.5-1.7-0.3-3.5,0.6-4.7c-1,0-2.1-0.4-2.8-1.2c-0.8-0.9-1.2-2.1-1.1-3.3 c0.1-0.8,0.6-1.7,1.1-2.7c0.7-1.5,1.5-3.1,1-4.1c-0.4-0.7-1-1.1-2-1.3l-0.3-0.1c-0.2,0-0.4-0.1-0.6-0.1c0.6,0.4,1,1.1,1.2,1.9 c0.2,1.3-0.4,3-1.4,4c0,0,0,0-0.1,0c0,0,0,0,0-0.1c0-0.1,0.1-0.3,0.2-0.4C12.8,11.2,13.2,10.3,13.3,9.6z M16.3,29.5 c-0.1,1.3,0.9,2.3,1.8,3.2c-0.4-1.1-0.5-1.4-1.2-2.4l0,0C16.6,29.9,16.4,29.6,16.3,29.5z M15.7,35.5C14.6,37,17.3,38.3,17,40 c0.1,0,0.3,0,0.4,0c0.7-1.5,2.1-3.3,1.8-4.8c-1.7,0-3.2-2.1-2.9-3.8l0,0h-0.1c-0.6,1.7-0.6,4.1,1.3,5.1C16.8,36.5,16,36.2,15.7,35.5 z M16.1,43.5c1.1,0.3,1.4-3,0.2-1.6c0.1-0.4,0.2-0.6,0.6-0.7C18.6,40.8,17.1,45.1,16.1,43.5z M25.3,42.4c0.1-1.4,2.4-1.3,1.6,0.2 h-0.1c0.5-1.3-1.4-0.6-1,0.4c0.1,0.4,0.4,0.6,0.8,0.3C26,44.8,25,43.2,25.3,42.4z M29.3,36.9L29.3,36.9c-0.7-0.1-1.4-0.4-1.7-0.8 c2.1,0.8,4.5-0.6,5.3-2.7l0,0c0,0.1,0,0.2,0,0.4C32.6,35.5,30.9,37.2,29.3,36.9z M40.6,28.3c-4.2,1.3-3.9-8-1-7.6 c-0.6-0.8-1.7-1-2.5-0.4c-1.2,0.1-1.5,2.1-2,2.8c0.1-1.2,0.6-2.3,1.4-3c-0.7-0.4-1.3-0.9-1.8-1.1l0,0c-0.6-0.3-1-0.5-1.6-0.8l0,0 c-4.3-2.2-10.4-1.6-13.8,2.4l-0.2,0.1c0.4-0.8,1-1.5,1.8-2.1c-3.5-2-4.7,3.9-3.2,6.1c0.7-0.3,1.4-0.1,2.1,0.4l-0.1,0.1 c-1.9-1-3.8,1.1-3.5,2.8c0.2,1.4,1.7,2.3,2,3.7c0.6,3.6,5,2.4,3.2-0.7c0.4,0.3,0.8,0.8,0.8,1.3c0.2-0.5,0.5-0.8,1-1 c-2.3,3.5,2.5,2.7,3.9,0.7c1.9-1.7,3.5-4.3,6-4.1c1.4,0.2,1.7,1.9,1,2.9c-0.4-6.7-6.7,2.6-8.9,3.4c-0.1,0.3-1.1,3.2-1.1,3.5L24,37.8 c-0.2-0.9-0.1-2.4,0.3-3.3c-0.9,0.1-2-0.5-2.1-1.3c-0.3,0.9-1.2,1.6-2.3,1.4c0.2,1,0,2,0,3.1c0,0.4,0,0.4-0.2,0.6 c0-0.6-0.1-1-0.2-1.7c-0.1,1-0.8,2.8-1.3,3.6c1.2,0.8,1.5,1.9,3.3,1.9c-0.6,0.3-1.4,0.1-2-0.1c0,1-0.1,2.2-0.4,0.4	c-2.6-5.5-5.8,4.2-0.2,2.3l0.1,0.1c-0.9,0.6-1.5,0.7-2.4,0.5c-0.6,1.2,0.1,2.9,1.6,3.1c2,0.6,4.8-2.4,6.5-1.1	c-0.9,0.1-1.9,0.3-2.7,0.6c3.7,0.4,6.3,1.8,7-3.1c0.6-2.3,3.4-8.4,5.4-4.9c-0.4-0.5-0.9-0.8-1.2-0.7c2.4,3.6,0.7,5.6-2.9,6.6 c1.5-0.6,4.6-1.7,3.1-5.5c0,0.4,0,1-0.4,1.5c0.9,0.3,0.1,2.3-0.7,2.1c-0.1,0.5-0.4,0.6-0.8,0.4c-0.1,0.4-0.6,0.9-1,0.5 c-0.1,0.3-0.6,0.6-0.9,0.5c-0.1,0.3,0,0.2-0.2,0.6c0,0.1,0.2,0,0.4,0c-1.1,0.6-0.8,2.2-2.1,2.7c-1.8,1.1-4.4-0.4-6.4-0.3 c-1.4,0.2-2.2,1.1-3.7,0.5c-0.3,4.8,2.8,0.7,4.9,1.9c-0.9,0-1.8,0.2-2.5,0.7c0.5,2.3,2.3,0.2,3.8,0.6c-0.3,0.1-0.3,0.1-0.7,0.3 c0.8,0,2.3,0.8,2.9-0.1c-1.5-0.2-2.2-1.4-3.7-2.1V50c8.6,4.1,4.8,0.6,8.4-3.1c0.7-0.7,2.1-0.8,3.4-1.6c1.2-0.8,2.3-3.7,1.3-5.4 c-0.9-1.7-0.8-2.2-1-2.7c-1.8-0.7-4.4,0.1-5.6,1.7l-0.1-0.1c0.9-1.7,3.5-2.7,5.3-2.4c1.6,0.2,1.2,2.3,2,3.4c2.2-0.9,2.4-3.6,1-5.3 c-0.7-1.6-1.4-3.6-0.4-4.9c0,0.7,0,1.4,0.1,2c0.2-0.9,0.6-1.5,1-1.8c-0.7-0.5-1.1-1.1-1.4-1.8C37.3,29.9,40,30.8,40.6,28.3z M21.8,30.3c0-1.2-0.3-2.4-0.9-3.5l0,0C21.9,27.8,22.4,29,21.8,30.3z M24.8,28c-0.2,0.2-0.4,0.5-0.6,0.8c-0.3,0.5-0.5,1-0.7,1.5 c0.1-1.4,0.8-3.3,2.6-3.3C25.7,27.2,25.2,27.6,24.8,28z M21.1,24.2c2.5,3.9,5-1,7.8-1.6c1-0.3,1.8-0.2,2.4,0.3 C27.4,21.2,23.5,30.1,21.1,24.2z M26.6,34.1c2.5-1.9,4.5-3.6,6.5-5.6c-0.7,2.4-0.3,7-4.1,6.7c-0.8,0-1.7-0.5-2.2-1 c-0.1,0.1-0.3,0.1-0.4,0.2c0,0-0.1,0-0.1,0.1C26.4,34.3,26.5,34.2,26.6,34.1C26.7,34.1,26.6,34.1,26.6,34.1z M25.3,44.4 c1.5,0.7,2.8-1.4,2.4-2.8c-0.9-2.1-3.7,0-4.2,1.7l0,0c0-0.6,0.2-1.2,0.6-1.7c-0.7,0.3-1.1,0.4-1.7,0.2c0.8,0,1.7-0.8,2.5-1.1 c-0.5-1.5,0.2-3.3,0.6-4.5h0.1l-0.1,0.3c-0.2,1-0.2,2.6,0,3.8C30,38.7,28.5,46.8,25.3,44.4z M35.2,29L35.2,29 c0.7-2.5,0.3-4.7-2.2-5.1c-0.1,0-0.9,0-0.9-0.1c1.4,0,2.5,0.2,3.3,1.2C36.3,26.2,35.9,27.8,35.2,29z M37,33c0.1,0.2,0.1,0.3,0.2,0.5 c0.1-0.1,0.2-0.1,0.2-0.2c1.2-2.9,5-0.9,6.8-3.2c0.6-0.4,2.5-2.5,3.8-3.3c-0.6,0-1.2,0-1.7-0.3c-1.5,0.9-3.1,1.6-5.1,1.8 c-0.3,1.3-1.6,2.8-3.1,2C37.5,31,37.3,32,37,33z M37.8,34.8C37.7,34.8,37.7,34.8,37.8,34.8L37.8,34.8c-0.3-0.4-0.5-0.8-0.6-1.1v-0.1 c0,0,0,0,0.1-0.1c0.5-0.1,2.2,0.1,3.4,0.2c0.3,0,0.5,0.1,0.7,0.1c-0.3-0.1-0.6-0.2-0.8-0.3c0,0-0.1,0-0.1-0.1c0,0,0-0.1,0.1-0.1 s0.1-0.1,0.2-0.1h0.1c1,0.2,2.6-0.5,3.5-1.3c-0.8,0.5-1.2,0.7-2.8,0.8c-0.1,0,2.7-0.5,4.5-2.8c0.8-1.1,2.7-2.3,2.5-2 C45.6,32.3,44.1,34.7,37.8,34.8L37.8,34.8z M41.2,32.3c-1.2,0.1-2.2,0.2-3,0.9L41.2,32.3z M15.7,31c0.1,0,0.2,0.1,0.2,0.1 c0-0.1,0.1-0.2,0.1-0.3c-0.2-0.6-0.3-1.2,0-1.8c-0.9-1.3,0-3.1,1.1-4c-0.1-0.1-0.1-0.3-0.2-0.4c-3.1-0.1-5.8-1.7-8.7-2.8 c0.2,4,3.5,4.1,5.7,5.4C14.9,27.8,15.8,28.9,15.7,31z M10.6,26.5c1,2.3,2.5,3.7,4.7,4.2c0-0.5-0.4-0.9-0.6-1.3 c-0.4,0.2-1,0.4-1.4,0.1C13.1,29.4,13,29.2,13,29c0,0,0-0.1,0.1-0.1c0.1,0,0.8,0.2,1.1-0.1c-0.4-0.2-0.4-0.3-0.8-0.3 c-0.6,0-1.3,0-1.6-0.5c0,0,0,0,0-0.1s0.8,0.1,1.4,0c0.2,0,0.1,0.1,0.3,0.1C13,27.7,11.5,27.2,10.6,26.5z"
            />
          </g>
        </svg>
      )}
    </div>
  )
}

// standalone simple loader
export const Loader = () => {
  const dots = 'dots h-2.5 w-2.5 bg-current rounded-full animate-pulse'

  return (
    <div className="Loader flex items-center justify-center gap-2 py-12 px-5">
      <span className={`${dots} delay-0`} />
      <span className={`${dots} delay-100`} />
      <span className={`${dots} delay-200`} />
    </div>
  )
}
